import {StopRecordingResult} from "@capgo/capacitor-audio-recorder";
import {Filesystem} from "@capacitor/filesystem";
import {Mime, PutAudioRequest} from "scribe-poc-shared";
import {DBSchema, openDB} from "idb";
import {v4} from "uuid";
import {firstValueFrom, Subject} from "rxjs";
import {Uploader, uploadOption} from "@capgo/capacitor-uploader";

/**
 * Class representing a recording that will be later uploaded to the server. It stores its audio data in either
 * IndexedDB (web) or on the file system (mobile). It also controls uploading in the background with signed URLs.
 */
export class RecordingToUpload {
	/** Internal id for this recording (used in web storage and as a temporary id for uploads) */
	readonly id:string = v4();

	/** Size of the audio in bytes */
	private _size:number;

	/** Mime type of the upload */
	private _type:string;

	/** The URI of where the audio is stored (idb:// or file://) */
	private _uri:string;

	/** Duration of the recording */
	private _duration:number

	/** Was this recording originally a blob (web)? */
	private isBlob:boolean;

	/** The appointment id of this recording */
	private _appointmentId:string;

	/** The id of an upload in progress for finding progress and status */
	private _uploadId:string;

	/** Async progress of the upload */
	private readonly subject = new Subject<void>();

	/** Has the upload been completed */
	private _isDone:boolean = false;

	/** The data sent to the server for an upload (so it can be retied) */
	private uploadOptions:uploadOption;

	/** Amount uploaded */
	percent:number = 0;

	/**
	 * Saves the recording data to the appropriate storage (IndexedDB for web, Filesystem for mobile).
	 * @param result The result from the audio recorder containing the blob or file URI.
	 */
	async save(result:StopRecordingResult) {
		this._duration = result.duration;

		if(result.blob) {
			this.isBlob = true;

			const db = await openDB<RecordingSchema>("scribe-recordings", 1, {
				upgrade: (db) => {
					db.createObjectStore("saved");
				}
			});

			await db.put("saved", result.blob, this.id);
			this._uri = `idb://scribe-recordings/saved/${this.id}`;
			this._size = result.blob.size;
			this._type = Mime.normalizeMime(result.blob.type);
		} else {
			this.isBlob = false;

			console.info(`Recording finished with file located at: ${result.uri}`);

			const stat = await RecordingToUpload.doStat(result.uri);
			this._size = stat.size;
			this._uri = result.uri;

			try {
				this._type = Mime.fileExtToMime(stat.name.split('.')[1])
			} catch(e) {
				console.warn(`Couldn't determine mime type of file ${result.uri}`, e);
			}
		}
	}

	/**
	 * Deletes the recording data from storage.
	 */
	async delete() {
		if(this.isBlob) {
			const path = new URL(this.uri);
			const parts = path.pathname.split('/');
			const filename = parts[parts.length - 1];

			const db = await openDB<RecordingSchema>("scribe-recordings");
			await db.delete("saved", filename);
		} else {
			await RecordingToUpload.doDelete(this.uri);
		}
	}

	/**
	 * Initiates the background upload process using a signed URL.
	 * @param signedUploadUrl The destination URL for the PUT request.
	 * @param data Metadata about the recording being uploaded.
	 */
	async upload(signedUploadUrl:string, data:PutAudioRequest) {
		this.uploadOptions = {
			headers: {},
			filePath: this.uri,
			maxRetries: 3,
			serverUrl: signedUploadUrl,
			method: "PUT",
			notificationTitle: "Uploading Recording",
			uploadType: "binary"
		};

		const {id} = await Uploader.startUpload(this.uploadOptions);
		console.info(`Upload of ${data.appointmentId}:${data.type}:${data.category} started with id ${id}`);

		this._appointmentId = data.appointmentId;
		this._uploadId = id;
	}

	/** Retry uploading the request */
	async retry() {
		const {id} = await Uploader.startUpload(this.uploadOptions);
		console.info(`Retrying uploading of ${this.appointmentId} started with id ${id}`);

		this._uploadId = id;
	}

	/**
	 * Marks the upload as complete, updates progress, and cleans up local storage.
	 * @param success whether the recording is being completed as a success (true) or error (false)
	 */
	async complete(success:boolean = true) {
		this.percent = 1;
		if(success) {
			this.subject.next();
		} else {
			this.subject.error("Upload failed")
		}
		this._isDone = true;

		await this.delete();

		console.info(`Finished upload #${this.uploadId}`);
	}

	/**
	 * Wrapper for {@link Filesystem.stat} to retrieve file metadata (so it can be spied on in testing).
	 * @param path The file URI.
	 */
	static doStat(path:string) {
		return Filesystem.stat({path});
	}

	/**
	 * Wrapper for {@link Filesystem.deleteFile} to remove a file (so it can be spied on in testing).
	 * @param path The file URI.
	 */
	static doDelete(path:string) {
		return Filesystem.deleteFile({path});
	}

	/** Size of the audio in bytes */
	get size():number {
		return this._size;
	}

	/** Mime type of the upload */
	get type():string {
		return this._type;
	}

	/** The URI of where the audio is stored (idb:// or file://) */
	get uri():string {
		return this._uri;
	}

	/** Duration of the recording */
	get duration():number {
		return this._duration;
	}

	/** The id of an upload in progress for finding progress and status */
	get uploadId():string {
		return this._uploadId;
	}

	/** The appointment id of this recording */
	get appointmentId() {
		return this._appointmentId;
	}

	/** Get a promise for this object completing */
	get onComplete() {
		return firstValueFrom(this.subject);
	}

	/** Has the upload been completed */
	get isDone():boolean {
		return this._isDone;
	}
}

/** Schema for IndexedDB storage for web */
interface RecordingSchema extends DBSchema {
	saved:{
		/** Internal id of the file stored */
		key:string,

		/** The audio file blob */
		value:Blob
	}
}
